import React, { useEffect, useState } from "react";
import ComboBox from "../components/ComboBox";
import { ExistingProvider } from "./ExistingProvider";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useAddProviderContact,
  useAddProviderLocation,
  useGetprovidersListForProviderDetails,
  useListOfProviderLocations,
  usePostAssessmentsRequests,
} from "../custom-hooks/queries";
import { existingProviderValidationSchema } from "../Schema/existingProviderSchema";
import { NewProvider } from "./NewProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import NotificationModal from "../components/NotificationModal";
import { useDispatch } from "react-redux";
import { setDrawer } from "../redux-store/features/layoutSlice";
import { newVendorRightsList } from "../utils/constants";

export const RequestNewAssessment = () => {
  const [address, setAddress] = useState("");
  const methods = useForm({
    resolver: yupResolver(existingProviderValidationSchema),
    mode: "onTouched",
    defaultValues: {
      providerType: "existingProvider", // default provider type
      selectedProvider: null, // no provider selected initially
      locationType: "primary",
      selectedAssessmentLocationId: "",
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = methods;

  const { data: providerLocations, refetch: refetchLocations } =
    useListOfProviderLocations(watch("selectedProvider")?.provider_id);

  const [pageToggle, setPageToggle] = useState("existingProvider");
  const { data: providers } = useGetprovidersListForProviderDetails();
  const [formData, setFormData] = useState({
    mainContactName: "",
    mainContactEmail: "",
  });
  const { mutate, error, isError,isPending } = usePostAssessmentsRequests();
  const { mutateAsync: addProviderContact } = useAddProviderContact();
  const { mutateAsync: addProviderLocation } = useAddProviderLocation();
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get("id");

  const [
    currentSelectedAssessmentContact,
    currentSetSelectedAssessmentContact,
  ] = useState(null);
  const [
    currentSelectedAssessmentLocation,
    currentSetSelectedAssessmentLocation,
  ] = useState(null);

  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [newLocation, setNewLocation] = useState(null);

  useEffect(() => {
    const autoFilledData = providers?.find(
      (item) => item.provider_id === assessmentId,
    );
    if (autoFilledData) {
      setValue("selectedProvider", autoFilledData);
    }
  }, [assessmentId, providers]);

  // Sync selected assessment location id to form for validation (primary location required only when "new-location")
  useEffect(() => {
    setValue(
      "selectedAssessmentLocationId",
      currentSelectedAssessmentLocation?.id ?? "",
    );
  }, [currentSelectedAssessmentLocation, setValue]);

  const onSubmit = async (data) => {
    const transformedCapabilities = data.serviceCategories?.map((service) => ({
      rfi_id: service.value,
      rfi_name: service.label,
    }));

    const location =
      currentSelectedAssessmentLocation?.id === "new-location"
        ? newLocation
        : currentSelectedAssessmentLocation;

    const assessmentContact =
      currentSelectedAssessmentContact?.id === "new-contact"
        ? additionalContacts[0]
        : currentSelectedAssessmentContact;

    const fullName =
      assessmentContact.firstName + " " + assessmentContact.lastName;

    // Determine location requirements based on radio button selection
    const isPrimaryLocationRequired = data?.locationType === "primary";
    const isSecondaryLocationRequired = data?.locationType === "secondary";

    const assessmentData = {
      providerId:
        data?.selectedProvider?.id || data?.selectedProvider?.provider_id || "",
      providerType: data?.selectedProvider?.provider_type || "",
      isPrimaryLocationRequired,
      isSecondaryLocationRequired,
      secondaryLocationAddress:
        data?.locationType === "secondary"
          ? data?.secondaryLocationTextBox || ""
          : "",
      capabilities: transformedCapabilities,
      desiredInitialDraftDate: data?.draftDate?.startDate || "",
      additionalDetails: data?.additionalDetails || "",
      isMultiSponsorAssessment: true,
      providerName: data?.selectedProvider?.name || "",
      providerMainContactName: fullName.trim() || "",
      providerMainContactEmail: assessmentContact?.email || "",
      providerUrl: data?.providerUrl || "",
      providerAddress1: location?.address || location?.address1 || "",
      providerCity: location?.city || "",
      providerCountry: location?.country || "",
      providerState: location?.state || "",
      providerZip: data?.zipCode || "",
      providerPhoneNumber: data?.providerPhoneNumber || "",
      providerAdditionalAddresses: data?.additionalAddress || "",
      providerAddress2: data?.providerAddress2 || "",
      vqaType: data?.onsiteRemote?.onsite === true ? "onsite" : "remote",
      otherAddress:
        data?.locationType === "other" ? data?.otheryLocationTextBox || "" : "",

      is_provider_new: false,
      provider_type: providerLocations[0]?.provider_type,
      contact: assessmentContact.email,
    };
    mutate(assessmentData, {
      onSuccess: async () => {
        const providerId = data.selectedProvider?.provider_id || "";
        const organization_id = data.selectedProvider?.org_id || "";
        const providerDataRoles = {
          provider_user_subrole: [
            {
              Subrole: "Vendor Admin",
            },
          ],
          provider_user_permissions: newVendorRightsList.map((permission) => ({
            permission: permission,
          })),
        };

        const vendorContacts =
          currentSelectedAssessmentContact?.id === "new-contact"
            ? [
                ...additionalContacts?.map((contact) => ({
                  provider_id: providerId,
                  organization_id,
                  email: contact.email,
                  first_name: contact.firstName,
                  last_name: contact.lastName,
                  job_title: contact.title,
                  is_primary: false,
                  provider_name: data?.selectedProvider?.name || "",
                  ...providerDataRoles,
                })),
              ]
            : [];
        // send contacts to endpoint (use a loop to call multiple times the endpoint)
        if (vendorContacts && vendorContacts.length > 0) {
          vendorContacts.forEach(async (contact) => {
            addProviderContact(contact);
          });
        }

        const vendorLocations =
          currentSelectedAssessmentLocation?.id === "new-location"
            ? [
                {
                  address1: newLocation?.address || "",
                  city: newLocation?.city || "",
                  country: newLocation?.country || "",
                  state: newLocation?.state || "",
                  provider_id: providerId,
                  location_type: "additional",
                },
              ]
            : [];
        // send locations to endpoint (use the new endpoint to send the list)
        if (vendorLocations && vendorLocations.length > 0) {
          await Promise.all(
            vendorLocations.map((location) => {
              return addProviderLocation({ provider_id: providerId, location });
            }),
          );
          refetchLocations();
        }

        setMessage(
          "Your request has been submitted to the admin for review. You will be notified once it is processed",
        );
        dispatch(setDrawer(true));
        setShow(true);

        setTimeout(() => {
          dispatch(setDrawer(false));
          navigate("/Assessment/History");
        }, [3000]);
      },
      onError: () => {
        setShow(true);
        setMessage("Something went wrong!");
      },
      onSettled: () => {
        setDrawer(false);
      },
    })
  };

  const onChangeNewLocation = (location) => {
    setNewLocation(location);
  };

  const onCancel = () => {
    reset(); // Reset form values
    setPageToggle("existingProvider"); // Reset to the default toggle state
  };
  const providerDetails = watch("selectedProvider");

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="container">
        <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-5">
          <h1 className="font-semibold">
            Request Audited Assessment Report (VQA)
          </h1>
          <p className="text-sm text-gray-600">
            Complete the information below to request a formal audited
            assessment report (VQA).
          </p>
        </div>
        <div className="px-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6">
          <div className="flex items-center space-x-3 w-full md:w-52">
            <input
              type="radio"
              value="existingProvider"
              name="providerType"
              checked={pageToggle === "existingProvider"}
              onClick={() => setPageToggle("existingProvider")}
              className="text-indigo-600 focus:ring-indigo-600 focus:ring-offset-2"
            />
            <label className="ml-3 block text-sm font-medium leading-6 text-gray-900">
              Existing Vendor
            </label>
          </div>

          {/* <div className="flex items-center space-x-3 w-full md:w-52">
            <input
              type="radio"
              value="newProvider"
              name="providerType"
              checked={pageToggle === "newProvider"}
              onClick={() => setPageToggle("newProvider")}
              className="text-indigo-600 focus:ring-indigo-600 focus:ring-offset-2"
            />
            <label className="ml-3 block text-sm font-medium leading-6 text-gray-900">
              New Vendor
            </label>
          </div>
          {errors.providerType && (
            <p className="text-red-500 text-sm">
              {errors.providerType.message}
            </p>
          )} */}

          {pageToggle === "existingProvider" && (
            <div className="w-full md:w-96">
              <ComboBox
                data={providers}
                placeHolder="Select Vendors"
                setSelected={(value) => setValue("selectedProvider", value)}
                selected={watch("selectedProvider")}
                width={"w-[500px]"}
                inputWidth={"w-[500px]"}
              />
              {errors.selectedProvider && (
                <p className="text-red-500 text-sm">
                  {errors.selectedProvider.message}
                </p>
              )}
            </div>
          )}
        </div>
        <div>
          {pageToggle === "existingProvider" ? (
            <ExistingProvider
              setFormData={setFormData}
              formData={formData}
              setOtherAddress={setAddress}
              otherAddress={address}
              providerDetails={providerDetails}
              locations={providerLocations}
              providerLocations={providerLocations && providerLocations[0]} // Pass the full provider data
              currentSelectedAssessmentContact={
                currentSelectedAssessmentContact
              }
              currentSetSelectedAssessmentContact={
                currentSetSelectedAssessmentContact
              }
              currentSelectedAssessmentLocation={
                currentSelectedAssessmentLocation
              }
              currentSetSelectedAssessmentLocation={
                currentSetSelectedAssessmentLocation
              }
              additionalContacts={additionalContacts}
              setAdditionalContacts={setAdditionalContacts}
              onChangeNewLocation={onChangeNewLocation}
              isPending={isPending}
            />
          ) : (
            <NewProvider />
          )}
        </div>
        <NotificationModal
          setOpen={setShow}
          open={show}
          title={isError ? "Error" : "Assessment Request Sent Successfully!"}
          desc={isError ? error?.response?.data?.message : message}
          error={isError}
        />{" "}
      </form>
    </FormProvider>
  );
};
